'use client';

import Image from 'next/image';
import { Check, Infinity as InfinityIcon, Sparkle as Astroid, Trophy, X } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/auth-context';
import { useLanguage } from '@/contexts/language-context';
import { useSessionContext } from '@/contexts/session-context';
import { normalizeUserLevel } from '@/lib/feature-permissions';
import { getPricingUrl } from '@/lib/pricing-url';
import { getSessionLimit, isUnlimitedSessionLimit } from '@/lib/session-permissions';
import { cn } from '@/lib/utils';

type MembershipLevel = ReturnType<typeof normalizeUserLevel>;
type Translate = ReturnType<typeof useLanguage>['t'];

type BenefitStatus = 'included' | 'unavailable' | 'text';

interface MembershipBenefit {
  label: string;
  status: BenefitStatus;
  value?: string;
  accent?: 'green' | 'orange';
  infinity?: boolean;
}

interface MembershipTier {
  level: MembershipLevel;
  badge: string;
  name: string;
  description: string;
  action: string;
  benefits: MembershipBenefit[];
}

function getMembershipTiers(t: Translate): MembershipTier[] {
  const benefitLabels = {
    sessions: t('membershipTiers.benefitChatSessions'),
    management: t('membershipTiers.benefitSessionManagement'),
    summary: t('membershipTiers.benefitDailySummary'),
    profile: t('membershipTiers.benefitTradingProfile'),
    deepAnalysis: t('membershipTiers.benefitDeepAnalysis'),
    chart: t('membershipTiers.benefitChartAnalysis'),
  };

  return [
    {
      level: 'L0',
      badge: 'L0',
      name: t('membershipTiers.tierL0Name'),
      description: t('membershipTiers.tierL0Description'),
      action: t('entitlementAccess.createAccount'),
      benefits: [
        {
          label: benefitLabels.sessions,
          status: 'text',
          value: t('membershipTiers.oneTemporarySession'),
        },
        { label: benefitLabels.management, status: 'unavailable' },
        {
          label: benefitLabels.summary,
          status: 'text',
          value: t('membershipTiers.listViewOnly'),
        },
        { label: benefitLabels.profile, status: 'unavailable' },
        { label: benefitLabels.deepAnalysis, status: 'unavailable' },
        { label: benefitLabels.chart, status: 'unavailable' },
      ],
    },
    {
      level: 'L1',
      badge: 'L1',
      name: t('membershipTiers.tierL1Name'),
      description: t('membershipTiers.tierL1Description'),
      action: t('entitlementAccess.purchaseChallenge'),
      benefits: [
        { label: benefitLabels.sessions, status: 'text', value: '3' },
        { label: benefitLabels.management, status: 'included' },
        {
          label: benefitLabels.summary,
          status: 'text',
          value: t('membershipTiers.availableT1'),
        },
        { label: benefitLabels.profile, status: 'included' },
        { label: benefitLabels.deepAnalysis, status: 'unavailable' },
        { label: benefitLabels.chart, status: 'included' },
      ],
    },
    {
      level: 'L2',
      badge: 'L2',
      name: t('membershipTiers.tierL2Name'),
      description: t('membershipTiers.tierL2Description'),
      action: t('entitlementAccess.purchaseAgain'),
      benefits: [
        { label: benefitLabels.sessions, status: 'text', value: '10' },
        { label: benefitLabels.management, status: 'included' },
        {
          label: benefitLabels.summary,
          status: 'included',
          value: t('membershipTiers.fullAccess'),
          accent: 'green',
        },
        { label: benefitLabels.profile, status: 'included' },
        {
          label: benefitLabels.deepAnalysis,
          status: 'included',
          value: t('membershipTiers.oncePerDay'),
          accent: 'green',
        },
        { label: benefitLabels.chart, status: 'included' },
      ],
    },
    {
      level: 'L3',
      badge: t('membershipTiers.tierL3Badge'),
      name: t('membershipTiers.tierL3Name'),
      description: t('membershipTiers.tierL3Description'),
      action: t('membershipTiers.allEntitlementsActive'),
      benefits: [
        {
          label: benefitLabels.sessions,
          status: 'text',
          value: t('membershipTiers.unlimited'),
          accent: 'orange',
          infinity: true,
        },
        { label: benefitLabels.management, status: 'included' },
        {
          label: benefitLabels.summary,
          status: 'included',
          value: t('membershipTiers.fullAccess'),
          accent: 'green',
        },
        { label: benefitLabels.profile, status: 'included' },
        {
          label: benefitLabels.deepAnalysis,
          status: 'included',
          value: t('membershipTiers.oncePerDay'),
          accent: 'green',
        },
        { label: benefitLabels.chart, status: 'included' },
      ],
    },
  ];
}

function BenefitValue({ benefit, t }: { benefit: MembershipBenefit; t: Translate }) {
  if (benefit.status === 'unavailable') {
    return (
      <span className="inline-flex items-center text-[#F05A19]">
        <X
          className="size-4"
          aria-hidden="true"
        />
        <span className="sr-only">{t('membershipTiers.unavailable')}</span>
      </span>
    );
  }

  if (benefit.status === 'included') {
    return (
      <span className={cn('inline-flex max-w-full flex-wrap items-center justify-end gap-1.5', benefit.accent === 'green' && 'text-[#16C94D]')}>
        <Check
          className="size-4 text-[#16C94D]"
          aria-hidden="true"
        />
        {benefit.value ? <span>{benefit.value}</span> : <span className="sr-only">{t('membershipTiers.included')}</span>}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex max-w-full flex-wrap items-center justify-end gap-1.5 text-white/90', benefit.accent === 'orange' && 'text-[#F98715]')}>
      {benefit.infinity && (
        <InfinityIcon
          className="size-5"
          aria-hidden="true"
        />
      )}
      {benefit.value}
    </span>
  );
}

interface MembershipTiersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MembershipTiersDialog({ open, onOpenChange }: MembershipTiersDialogProps) {
  const { user, isLogin, loading: authLoading } = useAuth();
  const { sessions, sessionStatus } = useSessionContext();
  const { isRTL, t } = useLanguage();
  const currentLevel = normalizeUserLevel(user?.level);
  const tiers = getMembershipTiers(t);
  const sessionLimit = getSessionLimit(user);
  const unlimitedSessions = isUnlimitedSessionLimit(sessionLimit);
  const displayName = isLogin ? user?.display_name?.trim() || user?.email?.split('@')[0]?.trim() || t('membershipTiers.memberDisplayName') : t('membershipTiers.guestDisplayName');
  const profileLabel = t(`membershipTiers.profile${currentLevel}`);
  const pricingUrl = getPricingUrl(user?.pricing_url);

  const openPricing = () => {
    if (pricingUrl && typeof window !== 'undefined') {
      window.open(pricingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        dir={isRTL ? 'rtl' : 'ltr'}
        overlayClassName="z-[89] bg-black/90"
        className="fixed inset-0 left-0 top-0 z-[90] flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-[#050301] p-0 text-white shadow-none sm:max-w-none">
        <div className="membership-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="sticky top-0 z-10 border-b border-white/[0.08] bg-[#050301] px-4 sm:px-6 lg:px-8">
            <div
              data-testid="membership-header-content"
              className="mx-auto flex w-full max-w-[1680px] items-center justify-between gap-2 py-3 sm:gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="relative size-11 shrink-0 overflow-hidden rounded-full border border-[#F98715]/45 bg-[#F98715] sm:size-12">
                  <Image
                    src="/AI-logoImage.png"
                    alt=""
                    fill
                    sizes="48px"
                    className="object-cover"
                    aria-hidden="true"
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-base font-semibold text-white sm:text-lg">{displayName}</span>
                    <span className="shrink-0 rounded-full border border-[#F98715]/40 bg-[#F98715]/15 px-2 py-0.5 text-xs text-[#F98715]">{currentLevel}</span>
                  </div>
                  <div className="truncate text-xs text-white/50 sm:text-sm">{profileLabel}</div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 sm:gap-5">
                <div
                  data-testid="membership-session-summary"
                  className="flex shrink-0 flex-col items-end gap-0.5 whitespace-nowrap text-[10px] leading-none text-white/65 min-[390px]:text-[11px] sm:flex-row sm:items-center sm:gap-1.5 sm:text-base sm:leading-normal">
                  <span>{t('membershipTiers.chatSessions')}:</span>
                  {unlimitedSessions ? (
                    <span className="inline-flex items-center gap-1 text-[#F98715]">
                      <InfinityIcon
                        className="size-4 sm:size-5"
                        aria-hidden="true"
                      />
                      {t('membershipTiers.unlimited')}
                    </span>
                  ) : (
                    <span className="text-[#F98715]">
                      {sessionStatus === 'ready'
                        ? t('membershipTiers.sessionUsage', { current: sessions.length, limit: sessionLimit })
                        : sessionStatus === 'error'
                          ? t('membershipTiers.unavailable')
                          : t('membershipTiers.loading')}
                    </span>
                  )}
                </div>
                <DialogClose asChild>
                  <button
                    type="button"
                    aria-label={t('membershipTiers.close')}
                    className="flex size-10 items-center justify-center rounded-xl border border-white/[0.1] bg-white/[0.06] text-white transition-colors hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F98715]/60 sm:size-11">
                    <X
                      className="size-5"
                      aria-hidden="true"
                    />
                  </button>
                </DialogClose>
              </div>
            </div>
          </div>

          <div className="px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
            <div className="mx-auto w-full max-w-[1680px]">
              <div className="text-center">
                <DialogTitle className="text-3xl font-bold tracking-[-0.03em] text-white sm:text-4xl lg:text-[42px]">{t('membershipTiers.title')}</DialogTitle>
                <DialogDescription className="mx-auto mt-3 max-w-5xl text-sm leading-6 text-white/55 sm:text-base lg:text-lg">{t('membershipTiers.subtitle')}</DialogDescription>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:mt-10 xl:grid-cols-4 xl:gap-5">
                {tiers.map((tier) => {
                  const isCurrentTier = tier.level === currentLevel;
                  const isVipTier = tier.level === 'L3';

                  return (
                    <section
                      key={tier.level}
                      data-membership-tier={tier.level}
                      data-current-tier={isCurrentTier ? 'true' : 'false'}
                      aria-current={isCurrentTier ? 'true' : undefined}
                      className={cn(
                        'flex min-h-[470px] flex-col rounded-[22px] border border-white/[0.16] bg-[#121212] p-5 shadow-[0_16px_45px_rgba(0,0,0,0.22)] sm:p-6',
                        isVipTier &&
                          'border-[#A25911] bg-[linear-gradient(180deg,#301801_0%,#201509_52%,#131211_100%)] shadow-[0_0_32px_rgba(249,135,21,0.16),0_0_12px_rgba(249,135,21,0.12)]',
                      )}>
                      <div>
                        <span
                          className={cn(
                            'inline-flex min-h-8 items-center rounded-full border px-[12px] text-sm',
                            isVipTier ? 'border-[#4C3A27] bg-[#392510] text-white/90' : 'border-white/[0.12] bg-white/[0.05] text-white/70',
                          )}>
                          {tier.badge}
                        </span>
                        <h2 className="mt-7 text-xl font-semibold text-white sm:text-[22px]">{tier.name}</h2>
                        <p className="mt-2 min-h-11 text-sm leading-5 text-white/55">{tier.description}</p>
                      </div>

                      <button
                        type="button"
                        disabled={isVipTier || authLoading || !pricingUrl}
                        onClick={openPricing}
                        className={cn(
                          'mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-full border border-white/[0.18] px-4 text-sm font-medium text-white transition-colors hover:border-[#F98715]/60 hover:bg-[#F98715]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F98715]/60 disabled:cursor-default disabled:opacity-45',
                          isVipTier && (isCurrentTier ? 'border-[#F98715] bg-[#F98715] text-white disabled:opacity-100' : 'border-transparent bg-[#6E3405]/55 text-white/45'),
                        )}>
                        {isVipTier && (
                          <Trophy
                            className="size-4"
                            aria-hidden="true"
                          />
                        )}
                        {tier.action}
                      </button>

                      <div className="mt-6 flex flex-1 flex-col justify-between gap-4">
                        {tier.benefits.map((benefit) => (
                          <div
                            key={benefit.label}
                            className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 text-sm sm:text-[15px]">
                            <span className="min-w-32 flex-1 text-white/70">{benefit.label}</span>
                            <span className="max-w-full text-end">
                              <BenefitValue
                                benefit={benefit}
                                t={t}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>

              <p className="mt-8 text-center text-xs leading-5 text-white/50 sm:text-sm">{t('membershipTiers.footerNote')}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MembershipTierEntry() {
  const [open, setOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const currentLevel = normalizeUserLevel(user?.level);
  const tierName = t(`membershipTiers.tier${currentLevel}Short`);

  return (
    <>
      <button
        type="button"
        disabled={authLoading}
        onClick={() => setOpen(true)}
        aria-label={t('membershipTiers.openLabel')}
        title={t('membershipTiers.openLabel')}
        className="flex size-8 shrink-0 items-center justify-center gap-1 rounded-lg border border-[#FB8516]/[0.28] bg-[#FF8514]/10 text-[#F98715] shadow-[0_5px_18px_rgba(249,135,21,0.1)] transition-colors hover:border-[#FB8516]/45 hover:bg-[#FF8514]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F98715]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-[#070400] disabled:cursor-wait disabled:opacity-50 min-[380px]:w-[86px] min-[380px]:px-2 sm:w-auto sm:min-w-[121px] sm:gap-1.5 sm:px-3">
        <Astroid
          className="size-3 shrink-0 min-[380px]:hidden sm:block sm:size-3.5"
          fill="currentColor"
          strokeWidth={0}
          aria-hidden="true"
        />
        <span className="hidden whitespace-nowrap text-[11px] font-semibold tracking-[-0.02em] min-[380px]:inline sm:text-xs sm:tracking-[-0.01em]">
          {t('membershipTiers.upgradePlan')}
        </span>
        <span className="sr-only">{t('membershipTiers.badge', { level: currentLevel, tier: tierName })}</span>
      </button>
      <MembershipTiersDialog
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
